"use client"

import { useEffect, useState } from "react"
import axios from "axios"
import * as XLSX from "xlsx"
import { motion, AnimatePresence } from "framer-motion"
import {
  Download as FiDownload,
  Search as FiSearch,
  Filter as FiFilter,
  Trash2 as FiTrash2,
  ChevronLeft as FiChevronLeft,
  ChevronRight as FiChevronRight,
  FileText as FiFileText,
  X as FiX,
  Plus as FiPlus,
  Sparkles,
  ToggleLeft as FiToggleLeft,
  ToggleRight as FiToggleRight
} from "lucide-react"
import {
  getDynamicFields,
  createDynamicField,
  toggleDynamicFieldStatus,
  deleteDynamicField,
  getAllDocumentation,
  deleteDocumentation
} from "../utils/api";

const DocumentationTable = () => {
  const [documentations, setDocumentations] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const [filter, setFilter] = useState("")
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [selectedDocId, setSelectedDocId] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedDocumentation, setSelectedDocumentation] = useState(null)
  const [detailDialogOpen, setDetailDialogOpen] = useState(false)
  const [notification, setNotification] = useState(null)
  const [fieldDefinitions, setFieldDefinitions] = useState([])
  const [activeTab, setActiveTab] = useState('documentations')
  const [newField, setNewField] = useState({
    fieldName: '',
    fieldType: 'text',
    label: '',
    placeholder: '',
    options: [],
    required: false,
    active: true
  })
  const [newOption, setNewOption] = useState('')
  const recordsPerPage = 20
  const [loading, setLoading] = useState({
    documentations: true,
    fields: true
  });

  const showNotification = (message, type = 'info') => {
    setNotification({ message, type })
    setTimeout(() => setNotification(null), 3000)
  }

  useEffect(() => {
    const fetchDocumentations = async () => {
      try {
        const data = await getAllDocumentation();
        setDocumentations(data);
      } catch (error) {
        showNotification(error.message, 'error');
      }
    };
    fetchDocumentations();
  }, []);

  useEffect(() => {
    const fetchFieldDefinitions = async () => {
      try {
        const data = await getDynamicFields();
        setFieldDefinitions(data.fields);
      } catch (error) {
        console.error('Error fetching field definitions:', error);
        showNotification('Failed to load field definitions', 'error');
      }
    };
    fetchFieldDefinitions();
  }, []);

  const handleDeleteConfirm = async () => {
    try {
      await deleteDocumentation(selectedDocId);
      setDocumentations(prev => prev.filter(doc => doc._id !== selectedDocId));
      setDeleteDialogOpen(false);
      showNotification('Documentation deleted successfully!', 'success');
    } catch (error) {
      showNotification(error.message, 'error');
    }
  };

  const extractCreatedField = (resp) => {
    // resp might already be the field object, or resp.field, resp.data, resp.data.field, etc.
    if (!resp) return null;

    // Common direct candidates
    const candidates = [
      resp,
      resp.field,
      resp.data,
      resp.data?.field,
      resp.result,
      resp.result?.field
    ];

    for (const c of candidates) {
      if (c && typeof c === 'object') {
        // accept objects that contain either an identifier or the expected key
        if ('fieldName' in c || '_id' in c || 'label' in c) return c;
      }
    }

    // Try a shallow search for a nested object containing fieldName/_id/label
    if (typeof resp === 'object') {
      for (const k of Object.keys(resp)) {
        const v = resp[k];
        if (v && typeof v === 'object' && ('fieldName' in v || '_id' in v || 'label' in v)) {
          return v;
        }
      }
    }

    return null;
  }

  const handleAddField = async () => {
    if (!newField.fieldName || !newField.label) {
      showNotification('Field name and label are required', 'error');
      return;
    }

    try {
      const data = await createDynamicField(newField);
      console.log('createDynamicField raw response:', data);

      const created = extractCreatedField(data);

      if (!created) {
        const optimistic = {
          ...newField,
          _id: 'temp-' + Date.now()
        };
        setFieldDefinitions(prev => [...prev.filter(Boolean), optimistic]);
        setNewField({
          fieldName: '',
          fieldType: 'text',
          label: '',
          placeholder: '',
          options: [],
          required: false,
          active: true
        });
        showNotification('Field added (optimistic). Server returned unexpected body — check console.', 'success');
        console.warn('Could not extract created field from response; logged above. Response:', data);
        return;
      }

      // Normalize minimal expected shape if required
      const normalized = {
        _id: created._id ?? created.id ?? created.fieldId ?? ('temp-' + Date.now()),
        fieldName: created.fieldName ?? created.name ?? created.key ?? newField.fieldName,
        fieldType: created.fieldType ?? created.type ?? newField.fieldType,
        label: created.label ?? created.title ?? newField.label,
        placeholder: created.placeholder ?? newField.placeholder,
        options: Array.isArray(created.options) ? created.options : (created.options ? [created.options] : newField.options || []),
        required: typeof created.required === 'boolean' ? created.required : !!newField.required,
        active: typeof created.active === 'boolean' ? created.active : true,
        ...created // keep any other server props
      }

      setFieldDefinitions(prev => [...prev.filter(Boolean), normalized]);
      setNewField({
        fieldName: '',
        fieldType: 'text',
        label: '',
        placeholder: '',
        options: [],
        required: false,
        active: true
      });
      showNotification('Field added successfully', 'success');
    } catch (error) {
      console.error('createDynamicField error:', error);
      showNotification(error?.message || 'Failed to create field', 'error');
    }
  };

  // Add this utility function to detect file URLs
  const isFileUrl = (value) => {
    if (typeof value !== 'string') return false;

    // Check if it's a URL that points to a file
    const urlPattern = /^https?:\/\/.+\.[a-zA-Z0-9]+$/;
    const fileExtensionPattern = /\.(pdf|doc|docx|txt|jpg|jpeg|png|gif|bmp|webp|zip|rar|mp4|mov|avi|mp3|wav|ogg|xls|xlsx|csv)$/i;

    return urlPattern.test(value) && fileExtensionPattern.test(value);
  };

  // Add this component for rendering file download buttons
  const FileDownloadButton = ({ url, fileName }) => {
    const handleDownload = () => {
      if (url) window.open(url, "_blank");
    };

    // Extract filename from URL if not provided
    const displayName = fileName || url?.split('/').pop() || 'Download';

    return (
      <button
        className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium bg-blue-100 text-blue-800 border border-blue-200 hover:bg-blue-200 transition-colors"
        onClick={handleDownload}
      >
        <FiDownload className="w-4 h-4 mr-1" />
        {displayName.length > 20 ? displayName.substring(0, 20) + '...' : displayName}
      </button>
    );
  };

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      const data = await toggleDynamicFieldStatus(id);
      setFieldDefinitions(prev => prev.map(f => f._id === id ? data.field : f));
      showNotification(`Field ${data.field.active ? 'activated' : 'deactivated'}`, 'success');
    } catch (error) {
      showNotification(error.message, 'error');
    }
  };

  const handleDeleteField = async (id) => {
    try {
      await deleteDynamicField(id);
      setFieldDefinitions(prev => prev.filter(f => f._id !== id));
      showNotification('Field deleted successfully', 'success');
    } catch (error) {
      showNotification(error.message, 'error');
    }
  };

  const handleDownloadPitchDeck = (url) => {
    if (url) window.open(url, "_blank")
  }

  const handleViewDetails = (doc) => {
    setSelectedDocumentation(doc)
    setDetailDialogOpen(true)
  }

  const handleDownloadExcel = () => {
    const headers = [
      'Date',
      'Startup Name',
      'Email',
      'Phone',
      'Industry',
      'Location',
      'Document Type',
      'Pitch Deck URL',
      ...fieldDefinitions.filter(f => f && f.active).map(f => f.label)
    ];

    const data = documentations.map(doc => {
      const dt = new Date(doc.createdAt);
      const date = dt.toLocaleDateString();

      return [
        date,
        doc.startupName,
        doc.email,
        doc.phone,
        doc.industry,
        doc.location,
        doc.route,
        doc.pitchDeck || '',
        ...fieldDefinitions.filter(f => f && f.active).map(f => doc.dynamicFields?.[f.fieldName] ?? '')
      ];
    });

    // Create worksheet
    const ws = XLSX.utils.aoa_to_sheet([headers, ...data])
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, "Documentations")
    XLSX.writeFile(wb, "Documentation_List.xlsx")
    showNotification('Excel file downloaded successfully!', 'success')
  }

  // Dynamic field handlers
  const handleAddOption = () => {
    if (newOption.trim() && !newField.options.includes(newOption.trim())) {
      setNewField(prev => ({
        ...prev,
        options: [...prev.options, newOption.trim()]
      }))
      setNewOption('')
    }
  }

  const handleRemoveOption = (option) => {
    setNewField(prev => ({
      ...prev,
      options: prev.options.filter(o => o !== option)
    }))
  }

  // Search & filter
  const filtered = documentations.filter((item) => {
    const term = searchTerm.toLowerCase()
    if (filter) return item[filter]?.toString().toLowerCase().includes(term)
    return Object.values(item).some((v) => v?.toString().toLowerCase().includes(term))
  })

  const totalPages = Math.ceil(filtered.length / recordsPerPage)
  const displayed = filtered.slice((currentPage - 1) * recordsPerPage, currentPage * recordsPerPage)

  const fieldTypes = [
    { value: 'text', label: 'Text Input' },
    { value: 'number', label: 'Number' },
    { value: 'date', label: 'Date' },
    { value: 'select', label: 'Dropdown' },
    { value: 'checkbox', label: 'Checkbox' },
    { value: 'file', label: 'File Upload' },
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-8 px-4">
      {/* Notification */}
      {notification && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className={`fixed top-4 left-1/2 transform -translate-x-1/2 px-6 py-3 rounded-xl shadow-lg z-50 ${notification.type === 'error' ? 'bg-red-100 text-red-800 border border-red-200' :
            notification.type === 'success' ? 'bg-green-100 text-green-800 border border-green-200' :
              'bg-blue-100 text-blue-800 border border-blue-200'
            }`}
        >
          <div className="flex items-center gap-2">
            {notification.type === 'error' && (
              <FiX className="w-5 h-5 text-red-600" />
            )}
            {notification.type === 'success' && (
              <FiFileText className="w-5 h-5 text-green-600" />
            )}
            <span>{notification.message}</span>
          </div>
        </motion.div>
      )}

      {/* Background Pattern */}
      <div className='absolute inset-0 bg-[url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fillRule="evenodd"%3E%3Cg fill="%239C92AC" fillOpacity="0.05"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")] opacity-40 pointer-events-none' />
      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="backdrop-blur-xl bg-white/80 border border-white/20 shadow-xl rounded-3xl p-6 mb-8"
        >
          <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
                <FiFileText className="text-white text-2xl" />
              </div>
              <div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                  Documentation Management
                </h1>
                <p className="text-gray-600 text-lg">Manage and track startup documentation</p>
              </div>
            </div>

            <div className="flex rounded-xl bg-gray-100 p-1">
              <button
                onClick={() => setActiveTab('documentations')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'documentations'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
                  }`}
              >
                Documentations
              </button>
              <button
                onClick={() => setActiveTab('fields')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'fields'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
                  }`}
              >
                Dynamic Fields
              </button>
            </div>
          </div>
        </motion.div>

        {activeTab === 'documentations' ? (
          <>
            {/* Filters */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="backdrop-blur-xl bg-white/80 border border-white/20 shadow-xl rounded-3xl p-6 mb-8"
            >
              <div className="flex items-center gap-3 mb-4">
                <FiFilter className="text-purple-600 text-xl" />
                <h2 className="text-xl font-semibold text-gray-800">Search & Filter</h2>
              </div>
              <div className="flex flex-col lg:flex-row gap-4 w-full">
                <div className="flex-1 space-y-2">
                  <label htmlFor="filter-by" className="text-sm font-semibold text-gray-700">
                    Filter By
                  </label>
                  <select
                    id="filter-by"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 text-lg transition-all duration-300"
                  >
                    <option value="">All</option>
                    <option value="startupName">Startup Name</option>
                    <option value="email">Email</option>
                    <option value="phone">Phone</option>
                    <option value="industry">Industry</option>
                    <option value="location">Location</option>
                    <option value="route">Documentation Type</option>
                  </select>
                </div>
                <div className="flex-1 space-y-2">
                  <label htmlFor="search-term" className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <FiSearch className="text-base" />
                    Search Term
                  </label>
                  <input
                    id="search-term"
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search documentations..."
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 text-lg transition-all duration-300"
                  />
                </div>
              </div>
            </motion.div>

            {/* Table */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="backdrop-blur-xl bg-white/80 border border-white/20 shadow-xl rounded-3xl p-6 mb-8 overflow-x-auto"
            >
              <div className="flex justify-end mb-4">
                <button
                  onClick={handleDownloadExcel}
                  className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <FiDownload className="w-4 h-4 mr-2" />
                  Export to Excel
                </button>
              </div>

              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {[
                      "Date",
                      "Startup Name",
                      "Email",
                      "Phone",
                      "Industry",
                      "Location",
                      "Document Type",
                      "Pitch Deck",
                      ...fieldDefinitions.filter(f => f && f.active).map(f => f.label), // Added null check
                      "Actions",
                    ].map((h) => (
                      <th
                        key={h}
                        scope="col"
                        className="px-4 py-3 text-left text-sm font-bold text-gray-700 uppercase tracking-wider"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {displayed.length === 0 ? (
                    <tr>
                      <td colSpan={9 + fieldDefinitions.filter(f => f && f.active).length} className="px-4 py-12 text-center text-gray-500 text-lg">
                        No documentations found. Try adjusting your search or filter criteria.
                      </td>
                    </tr>
                  ) : (
                    displayed.map((doc) => {
                      const dt = new Date(doc.createdAt)
                      const date = dt.toLocaleDateString()
                      return (
                        <tr key={doc._id} className="hover:bg-gray-50 transition-colors duration-150">
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-800">{date}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                            {doc.startupName}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-800">{doc.email}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-800">{doc.phone}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-800">{doc.industry}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-800">{doc.location}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-800 capitalize">
                            {doc.route}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {doc.pitchDeck ? (
                              <button
                                className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium bg-blue-100 text-blue-800 border border-blue-200 hover:bg-blue-200 transition-colors"
                                onClick={() => handleDownloadPitchDeck(doc.pitchDeck)}
                              >
                                <FiDownload className="w-4 h-4 mr-1" /> Download
                              </button>
                            ) : (
                              <span className="text-gray-400 text-sm">N/A</span>
                            )}
                          </td>

                          {/* Dynamic fields columns */}
                          {fieldDefinitions.filter(f => f && f.active).map(field => {
                            const fieldValue = doc.dynamicFields?.[field.fieldName];
                            const isFile = isFileUrl(fieldValue);

                            return (
                              <td key={field._id ?? field.fieldName} className="px-4 py-3 whitespace-nowrap text-sm text-gray-800">
                                {isFile ? (
                                  <FileDownloadButton url={fieldValue} />
                                ) : (
                                  fieldValue?.toString() || '-'
                                )}
                              </td>
                            );
                          })}

                          <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex gap-2">
                              <button
                                className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium bg-purple-100 text-purple-800 border border-purple-200 hover:bg-purple-200 transition-colors"
                                onClick={() => handleViewDetails(doc)}
                              >
                                <FiFileText className="w-4 h-4 mr-1" />
                                Details
                              </button>
                              <button
                                className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium bg-red-100 text-red-800 border border-red-200 hover:bg-red-200 transition-colors"
                                onClick={() => {
                                  setSelectedDocId(doc._id)
                                  setDeleteDialogOpen(true)
                                }}
                              >
                                <FiTrash2 className="w-4 h-4 mr-1" />
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      )
                    })
                  )}
                </tbody>
              </table>
            </motion.div>

            {/* Pagination */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="backdrop-blur-xl bg-white/80 border border-white/20 shadow-xl rounded-3xl p-6 flex flex-col sm:flex-row justify-between items-center gap-4"
            >
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((cp) => cp - 1)}
                className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all duration-300 text-lg ${currentPage === 1
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white text-gray-700 hover:bg-purple-50 hover:text-purple-600 shadow-sm hover:shadow-md"
                  }`}
              >
                <FiChevronLeft className="text-xl" />
                Previous
              </motion.button>
              <div className="flex items-center gap-2">
                <span className="text-base text-gray-600">
                  Page <span className="font-semibold text-purple-600">{currentPage}</span> of{" "}
                  <span className="font-semibold text-purple-600">{totalPages || 1}</span>
                </span>
              </div>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                disabled={currentPage === totalPages || totalPages === 0}
                onClick={() => setCurrentPage((cp) => cp + 1)}
                className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all duration-300 text-lg ${currentPage === totalPages || totalPages === 0
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white text-gray-700 hover:bg-purple-50 hover:text-purple-600 shadow-sm hover:shadow-md"
                  }`}
              >
                Next
                <FiChevronRight className="text-xl" />
              </motion.button>
            </motion.div>
          </>
        ) : (
          /* Dynamic Fields Management */
          <div className="bg-white rounded-2xl shadow-md p-6 mb-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Dynamic Fields Management</h2>
            </div>

            {/* Add New Field Form */}
            <div className="bg-gray-50 rounded-xl p-4 mb-8">
              <h3 className="text-lg font-semibold text-gray-700 mb-4">Add New Field</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Field Name (unique identifier)</label>
                  <input
                    type="text"
                    value={newField.fieldName}
                    onChange={(e) => setNewField({ ...newField, fieldName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., fundingAmount"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Field Type</label>
                  <select
                    value={newField.fieldType}
                    onChange={(e) => setNewField({ ...newField, fieldType: e.target.value, options: e.target.value === 'select' ? newField.options : [] })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {fieldTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Label (display text)</label>
                  <input
                    type="text"
                    value={newField.label}
                    onChange={(e) => setNewField({ ...newField, label: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., Funding Amount"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Placeholder (optional)</label>
                  <input
                    type="text"
                    value={newField.placeholder}
                    onChange={(e) => setNewField({ ...newField, placeholder: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., Enter funding amount"
                  />
                </div>
              </div>

              {newField.fieldType === 'select' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Options</label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={newOption}
                      onChange={(e) => setNewOption(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddOption()}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Add option and press Enter"
                    />
                    <button
                      onClick={handleAddOption}
                      className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <FiPlus size={16} />
                    </button>
                  </div>
                  {newField.options.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {newField.options.map(option => (
                        <div key={option} className="inline-flex items-center bg-gray-100 rounded-full px-3 py-1">
                          <span className="mr-2">{option}</span>
                          <button
                            onClick={() => handleRemoveOption(option)}
                            className="text-gray-500 hover:text-red-500"
                          >
                            <FiX size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <label className="inline-flex items-center">
                    <input
                      type="checkbox"
                      checked={newField.required}
                      onChange={(e) => setNewField({ ...newField, required: e.target.checked })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">Required Field</span>
                  </label>
                </div>

                <button
                  onClick={handleAddField}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                >
                  <FiPlus className="mr-2" size={16} />
                  Add Field
                </button>
              </div>
            </div>

            {/* Existing Fields List */}
            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-4">Current Fields</h3>

              {fieldDefinitions.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No dynamic fields have been created yet
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Field Name</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Label</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Required</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {fieldDefinitions.filter(Boolean).map((field) => (
                        <tr key={field._id}>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{field.fieldName}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{field.label}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 capitalize">
                            {fieldTypes.find(t => t.value === field.fieldType)?.label || field.fieldType}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                            {field.required ? (
                              <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                Required
                              </span>
                            ) : (
                              <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                                Optional
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                            <button
                              onClick={() => handleToggleStatus(field._id, field.active)}
                              className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${field.active ? 'bg-blue-600' : 'bg-gray-200'
                                }`}
                            >
                              <span
                                className={`inline-block w-4 h-4 transform transition-transform rounded-full bg-white ${field.active ? 'translate-x-6' : 'translate-x-1'
                                  }`}
                              />
                            </button>
                            <span className="ml-2 text-sm">
                              {field.active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                            <button
                              onClick={() => handleDeleteField(field._id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              <FiTrash2 size={16} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Delete Dialog */}
        <AnimatePresence>
          {deleteDialogOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[2147483647] p-4"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                className="bg-white rounded-3xl w-full max-w-md shadow-2xl"
              >
                <div className="p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <FiTrash2 className="text-red-600 text-2xl" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">Confirm Deletion</h3>
                      <p className="text-sm text-gray-600">This action cannot be undone</p>
                    </div>
                  </div>
                  <p className="text-gray-700 text-base mb-6">Are you sure you want to delete this documentation?</p>
                  <div className="flex gap-3">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setDeleteDialogOpen(false)}
                      className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors duration-200 text-base"
                    >
                      Cancel
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={handleDeleteConfirm}
                      className="flex-1 px-4 py-2.5 bg-gradient-to-r from-red-600 to-pink-600 text-white rounded-xl font-medium hover:shadow-lg transition-all duration-300 text-base"
                    >
                      Yes, Delete
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Documentation Details Dialog */}
        <AnimatePresence>
          {detailDialogOpen && selectedDocumentation && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[2147483647] p-4"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                className="bg-white rounded-3xl w-full max-w-2xl shadow-2xl max-h-[90vh] overflow-y-auto"
              >
                <div className="sticky top-0 bg-white border-b border-gray-100 p-6 rounded-t-3xl">
                  <div className="flex items-center justify-between">
                    <h2 className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                      Documentation Details
                    </h2>
                    <motion.button
                      whileHover={{ scale: 1.1, rotate: 90 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => setDetailDialogOpen(false)}
                      className="w-10 h-10 bg-gray-100 hover:bg-red-100 rounded-full flex items-center justify-center transition-colors duration-300"
                    >
                      <FiX className="text-gray-600 hover:text-red-600" />
                    </motion.button>
                  </div>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div>
                        <p className="text-sm text-gray-500">Startup Name</p>
                        <p className="text-lg font-medium text-gray-900">{selectedDocumentation.startupName}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Email</p>
                        <p className="text-lg font-medium text-gray-900">{selectedDocumentation.email}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Phone</p>
                        <p className="text-lg font-medium text-gray-900">{selectedDocumentation.phone}</p>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <p className="text-sm text-gray-500">Industry</p>
                        <p className="text-lg font-medium text-gray-900">{selectedDocumentation.industry}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Location</p>
                        <p className="text-lg font-medium text-gray-900">{selectedDocumentation.location}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Documentation Type</p>
                        <p className="text-lg font-medium text-gray-900 capitalize">{selectedDocumentation.route}</p>
                      </div>
                    </div>
                  </div>

                  {/* Dynamic Fields Section */}
                  {fieldDefinitions.filter(f => f && f.active).map(field => {
                    const fieldValue = selectedDocumentation.dynamicFields?.[field.fieldName];
                    const isFile = isFileUrl(fieldValue);

                    return (
                      <div key={field._id ?? field.fieldName}>
                        <p className="text-sm text-gray-500">{field.label}</p>
                        {isFile ? (
                          <div className="mt-1">
                            <FileDownloadButton url={fieldValue} />
                          </div>
                        ) : (
                          <p className="text-lg font-medium text-gray-900">
                            {fieldValue?.toString() || 'Not provided'}
                          </p>
                        )}
                      </div>
                    );
                  })}

                  <div className="mt-6">
                    <p className="text-sm text-gray-500 mb-2">Pitch Deck</p>
                    {selectedDocumentation.pitchDeck ? (
                      <button
                        className="inline-flex items-center px-4 py-2 rounded-lg text-base font-medium bg-blue-100 text-blue-800 border border-blue-200 hover:bg-blue-200 transition-colors"
                        onClick={() => handleDownloadPitchDeck(selectedDocumentation.pitchDeck)}
                      >
                        <FiDownload className="w-5 h-5 mr-2" /> Download Pitch Deck
                      </button>
                    ) : (
                      <p className="text-gray-400">No pitch deck available</p>
                    )}
                  </div>
                </div>
                <div className="p-6 border-t border-gray-100 flex justify-end">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setDetailDialogOpen(false)}
                    className="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors duration-200 text-base"
                  >
                    Close
                  </motion.button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}

export default DocumentationTable